// 移植自 crm/src/components/Account.jsx：頂欄右側帳號選單（修改密碼、主題切換、登出）＋修改密碼彈窗。
import { useEffect, useRef, useState } from 'react'
import { useFocusDropdown } from '@/hooks/useFocusDropdown'
import { IoChevronDownOutline } from '@/components/ui/ionicons'
import { account, logout } from '@/lib/auth'
import { request, ApiError } from '@/lib/request'
import { t } from '@/i18n'
import { Dialog } from '@/components/ui/Dialog'
import { FormField } from '@/components/ui/FormField'
import { CommonButton } from '@/components/ui/CommonButton'
import { KeyIcon, LogoutIcon, SunIcon, MoonIcon, EyeIcon, EyeOffIcon, OtpIcon } from '@/components/ui/icons'

// 觸控裝置沒有 hover：dropdown-hover 會「點一下開了又關」。只在滑鼠裝置保留 hover 觸發，觸控靠點擊（focus-within）開合
const canHover = window.matchMedia('(hover: hover)').matches

export function Account() {
  // 初始主題已由 main.jsx 在 render 前寫進 body（判斷同舊版）
  const [isDark, setIsDark] = useState(() => document.body.dataset.theme === 'dark')
  const [pwOpen, setPwOpen] = useState(false)
  const dd = useFocusDropdown() // Esc 收起選單

  function toggleTheme(toDark) {
    setIsDark(toDark)
    document.body.dataset.theme = localStorage.theme = toDark ? 'dark' : 'light'
  }

  return (
    <>
      <div className={`dropdown dropdown-end ${canHover ? 'dropdown-hover' : ''} ${dd.closeClass}`} {...dd.rootProps}>
        <div tabIndex={0} role="button" className="account-trigger" {...dd.triggerProps}>
          <span className="hidden md:block account-trigger__name">{account.nickname}</span>
          <IoChevronDownOutline size={16} />
        </div>
        {/* daisyUI dropdown 靠 focus-within 開合，選單本身要能聚焦 */}
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
        <ul tabIndex={0} className="dropdown-content account-menu">
          <li>
            <button type="button" className="account-menu__item" onClick={() => setPwOpen(true)}>
              <span className="account-menu__leading">
                <KeyIcon className="size-4" />
              </span>
              <span>{t('app', 'Change Password')}</span>
            </button>
          </li>
          <li className="account-menu__divider" aria-hidden="true" />
          <li>
            <div className="account-menu__row">
              <span>{t('app', 'Theme')}</span>
              <div className="account-menu__theme-toggle">
                <button type="button" className={`account-menu__theme-btn ${!isDark ? 'is-active' : ''}`} aria-label="Light" onClick={() => toggleTheme(false)}>
                  <SunIcon />
                </button>
                <button type="button" className={`account-menu__theme-btn ${isDark ? 'is-active' : ''}`} aria-label="Dark" onClick={() => toggleTheme(true)}>
                  <MoonIcon />
                </button>
              </div>
            </div>
          </li>
          <li className="account-menu__divider" aria-hidden="true" />
          <li>
            <button type="button" className="account-menu__item" onClick={() => logout()}>
              <span className="account-menu__leading">
                <LogoutIcon className="size-4" />
              </span>
              <span>{t('app', 'Sign Out')}</span>
            </button>
          </li>
        </ul>
      </div>

      {/* 關閉時卸載：每次打開都是全新狀態（舊版是開啟時手動清空四欄與錯誤） */}
      {pwOpen && <EditPasswordDialog onClose={() => setPwOpen(false)} />}
    </>
  )
}

// 密碼欄共用列：前導 KeyIcon + input + 尾端眼睛（Figma Input:margin h22 / justify-between）
function PasswordRow({ name, value, onChange, visible, onToggleVisible, inputRef, tabIndex }) {
  return (
    <div className="flex h-[22px] items-center gap-1">
      <KeyIcon className="size-4 shrink-0 text-brand-black-600" />
      <input
        ref={inputRef}
        type={visible ? 'text' : 'password'}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-[22px] w-full min-w-0 bg-transparent text-sm text-brand-black-1000 outline-none"
        required
        // 同舊版用正數 tabIndex（呼叫端傳 1~4）：Tab 依序走四個輸入欄再到送出鈕，跳過各欄的眼睛按鈕
        tabIndex={tabIndex}
      />
      <button type="button" onClick={onToggleVisible} aria-label={t('form', 'View password')} className="shrink-0 text-brand-black-600 transition-colors hover:text-brand-black-800">
        {visible ? <EyeIcon className="size-4" /> : <EyeOffIcon className="size-4" />}
      </button>
    </div>
  )
}

// 修改密碼彈窗，依 Figma node 9605:133392：共用 Dialog（max-w-480、body p-6）、四欄 FormField 間距 12、送出鈕上距 20。
// icon 16 框、色 brand-black-600；三個密碼欄各有一顆眼睛，OTP 欄沒有。
function EditPasswordDialog({ onClose }) {
  const [values, setValues] = useState({ old_password: '', password: '', confirm_password: '', otp: '' })
  const [sending, setSending] = useState(false)
  const [errors, setErrors] = useState({})
  const [eyesOpen, setEyesOpen] = useState({})
  const firstInput = useRef(null)

  const setField = (name) => (value) => setValues((prev) => ({ ...prev, [name]: value }))
  const toggleEye = (name) => setEyesOpen((prev) => ({ ...prev, [name]: !prev[name] }))
  // 四欄皆必填，未齊送出鈕 disabled
  const canSubmit = values.old_password && values.password && values.confirm_password && values.otp

  // 同舊版開啟後聚焦第一欄（Dialog 會先把焦點移進卡片，這裡在它之後再移到輸入欄）
  useEffect(() => {
    const timer = setTimeout(() => firstInput.current?.focus(), 0)
    return () => clearTimeout(timer)
  }, [])

  async function submit(e) {
    e.preventDefault()
    if (sending) return
    setSending(true)
    try {
      await request.put('/api/v1/change-password', values)
      document.location.reload()
    } catch (err) {
      // 同舊版：後端回的是各欄位的錯誤 key（或 error），顯示在對應欄位下
      if (err instanceof ApiError) setErrors(err.data ?? {})
      else throw err
    } finally {
      setSending(false)
    }
  }

  const rowProps = (name, tabIndex) => ({
    name,
    value: values[name],
    onChange: setField(name),
    visible: !!eyesOpen[name],
    onToggleVisible: () => toggleEye(name),
    tabIndex,
  })

  return (
    <Dialog open onClose={() => !sending && onClose()} busy={sending} title={t('app', 'Change Password')}>
      <form onSubmit={submit} className="flex flex-col">
        <div className="flex flex-col gap-3">
          <FormField label={t('form', 'Old Password')} error={errors.old_password && t('form', errors.old_password)}>
            <PasswordRow {...rowProps('old_password', 1)} inputRef={firstInput} />
          </FormField>
          <FormField label={t('form', 'New Password')} error={errors.password && t('form', errors.password)}>
            <PasswordRow {...rowProps('password', 2)} />
          </FormField>
          <FormField label={t('form', 'Confirm New Password')} error={errors.confirm_password && t('form', errors.confirm_password)}>
            <PasswordRow {...rowProps('confirm_password', 3)} />
          </FormField>
          {/* OTP 欄無眼睛，Input:margin 為 flex gap-4（非 justify-between） */}
          <FormField label={t('form', 'OTP')} error={errors.otp && t('form', errors.otp)}>
            <div className="flex h-[22px] items-center gap-1">
              <OtpIcon className="size-4 shrink-0 text-brand-black-600" />
              {/* type=number 同舊版：輸入層即擋非數字；no-spin 隱藏上下箭頭 */}
              <input
                type="number"
                name="otp"
                value={values.otp}
                onChange={(e) => setField('otp')(e.target.value)}
                className="no-spin h-[22px] w-full min-w-0 bg-transparent text-sm text-brand-black-1000 outline-none"
                required
                // eslint-disable-next-line jsx-a11y/tabindex-no-positive
                tabIndex={4}
              />
            </div>
          </FormField>
        </div>

        {errors.error && <p className="mt-2 text-xs leading-4.5 tracking-[0.12px] text-error">{t('form', errors.error)}</p>}

        {/* Submit Button（9605:133414）：pt 20 */}
        <div className="pt-5">
          {/* eslint-disable-next-line jsx-a11y/tabindex-no-positive -- 接在四個輸入欄（1~4）之後，見 PasswordRow */}
          <CommonButton type="submit" className="w-full" tabIndex={5} disabled={!canSubmit} loading={sending}>
            {t('form', 'Save')}
          </CommonButton>
        </div>
      </form>
    </Dialog>
  )
}
